import React from 'react';
import { LDReactContext } from '@launchdarkly/react-sdk';
import { Claim, Leave, PersonData } from '../../data-models';

export interface IFormContext extends PersonData {
  claimList?: Array<Claim | Leave>;
  dates?: {
    now: Date;
  };
  currentLeavePlan?: {
    planId: string;
  };
  stepIndexWithLeavePlanGraph?: number;
  [name: string]: any;
  paymentMethod?: string;
  smsOptIn?: string;
  config?: { [name: string]: string };
  leaveOnly?: boolean;
  hasVBCoverage?: boolean;
  isVBEmployerEnabled?: boolean;
  hidePaymentPrefs?: string;
  leavePlanSuccessfullyGenerated?: string;
  noApplicableNextSteps?: boolean;
  ptoChoice?: string;
  ldVariation?: (key: string, defaultValue: boolean) => boolean;
}

export type TFormContextState = {
  formContext: IFormContext;
  setFormContext: React.Dispatch<React.SetStateAction<any>>;
  allowNavBack: boolean;
  setAllowNavBack: React.Dispatch<React.SetStateAction<boolean>>;
  allowNavForward: boolean;
  setAllowNavForward: React.Dispatch<React.SetStateAction<boolean>>;
};

export const DefaultFormContextState = {
  formContext: {},
  setFormContext: () => {},
  allowNavBack: true,
  setAllowNavBack: () => {},
  allowNavForward: true,
  setAllowNavForward: () => {},
};

export const FormContext = React.createContext<TFormContextState>(
  DefaultFormContextState,
);

export const FormContextProvider = (p: {
  children: any;
  formContext?: { [name: string]: any };
}) => {
  const [baseFormContext, setBaseFormContext] = React.useState<IFormContext>(
    p.formContext ?? {},
  );
  // The LD provider replaces this value on initialisation and after identify()
  const ld = React.useContext(LDReactContext);
  const ldVariation = React.useCallback(
    (key: string, defaultValue: boolean) =>
      ld?.client?.boolVariation?.(key, defaultValue) ?? defaultValue,
    [ld],
  );
  const formContext = React.useMemo(
    () => ({ ...baseFormContext, ldVariation }),
    [baseFormContext, ldVariation],
  );
  const [allowNavBack, setAllowNavBack] = React.useState(true);
  const [allowNavForward, setAllowNavForward] = React.useState(true);

  React.useEffect(() => {
    if (p.formContext) {
      setBaseFormContext(p.formContext);
    }
  }, [p.formContext]);

  return (
    <FormContext.Provider
      value={{
        formContext,
        setFormContext: setBaseFormContext,
        allowNavBack,
        setAllowNavBack,
        allowNavForward,
        setAllowNavForward,
      }}
    >
      {p.children}
    </FormContext.Provider>
  );
};
